import { QuestionType } from '../../../generated/hooks';
import { ColumnType, type TableColumn } from '../components/AnswerGroupTable';
import { isMultipleChoice } from '@arnold/core';
import { inferQuestionScoring } from '../questionScoring';
import type { ReportQuestion, TextSource } from '../types';
import type { ReportMetricDefinition } from '../definition/types';

export type TablePreset = 'metrics' | 'choices' | 'text' | 'wide';
export const TABLE_PRESETS: TablePreset[] = ['metrics', 'choices', 'text', 'wide'];

export const GROUP_COLUMNS = [
  ColumnType.LABEL,
  ColumnType.TEAM,
  ColumnType.RESPONDENT_COUNT,
  ColumnType.ANSWER_COUNT,
  ColumnType.RESPONSE_RATE,
];

const isChoiceQuestion = (question: ReportQuestion) =>
  [QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale].includes(question.type) &&
  Boolean(question.answersLocal?.length);

const isScoreCandidate = (question: ReportQuestion) =>
  isChoiceQuestion(question) && !isMultipleChoice(question) && (question.answersLocal?.length ?? 0) >= 1;

const isNpsCandidate = (question: ReportQuestion) =>
  isScoreCandidate(question) && [10, 11].includes(question.answersLocal?.length ?? 0);

export const getTextSources = (question?: ReportQuestion, hasComments = false): TextSource[] => {
  if (!question) return [];
  if (question.type === QuestionType.Freetext) return hasComments ? ['text', 'comment'] : ['text'];
  return isChoiceQuestion(question) && hasComments ? ['comment'] : [];
};

export const isTextColumn = (
  column: TableColumn,
): column is Extract<TableColumn, { type: ColumnType.TEXT | ColumnType.THEMES }> =>
  column.type === ColumnType.TEXT || column.type === ColumnType.THEMES;

function getCompatibleColumnTypes(question?: ReportQuestion, hasComments = false): ColumnType[] {
  const types = [...GROUP_COLUMNS];
  if (!question) return types;
  if (isChoiceQuestion(question)) types.push(ColumnType.CHOICES);
  if (isScoreCandidate(question)) types.push(ColumnType.SCORE);
  if (isNpsCandidate(question)) types.push(ColumnType.NPS, ColumnType.MINI_NPS);
  if (getTextSources(question, hasComments).length) types.push(ColumnType.TEXT, ColumnType.THEMES);
  return types;
}

function makeTableColumn(
  id: string,
  type: ColumnType,
  {
    question,
    statementIndex,
    source,
    hasComments = false,
  }: {
    question?: ReportQuestion;
    statementIndex?: number;
    source?: TextSource;
    hasComments?: boolean;
  } = {},
): TableColumn | null {
  if (!getCompatibleColumnTypes(question, hasComments).includes(type)) return null;
  const base = { id, label: '' };
  // Non-matrix questions must never inherit the previous builder selection's statement binding.
  const statement =
    statementIndex !== undefined &&
    Number.isInteger(statementIndex) &&
    statementIndex >= 0 &&
    statementIndex < (question?.statementsLocal?.length ?? 0)
      ? statementIndex
      : undefined;
  switch (type) {
    case ColumnType.LABEL:
    case ColumnType.TEAM:
    case ColumnType.RESPONDENT_COUNT:
    case ColumnType.ANSWER_COUNT:
    case ColumnType.RESPONSE_RATE:
      return { ...base, type };
    case ColumnType.CUSTOM_METRIC:
      return null;
    case ColumnType.QUESTION_ANSWER_COUNT:
    case ColumnType.NPS:
    case ColumnType.MINI_NPS:
    case ColumnType.SCORE:
    case ColumnType.CHOICES:
      return question ? { ...base, type, questionIndex: question.index, statementIndex: statement } : null;
    case ColumnType.TEXT:
    case ColumnType.THEMES: {
      const sources = getTextSources(question, hasComments);
      const selectedSource = source ?? sources[0];
      return question && sources.includes(selectedSource)
        ? { ...base, type, questionIndex: question.index, statementIndex: statement, source: selectedSource }
        : null;
    }
  }
}

export function getPresetQuestion(preset: TablePreset, questions: ReportQuestion[], selected?: ReportQuestion) {
  const compatible = (question: ReportQuestion) =>
    preset === 'text'
      ? question.type === QuestionType.Freetext
      : preset === 'choices'
        ? isChoiceQuestion(question)
        : isScoreCandidate(question);
  if (selected && compatible(selected)) return selected;
  if (preset === 'metrics' || preset === 'wide') {
    return (
      questions.find((question) => inferQuestionScoring(question).nps) ??
      questions.find((question) => inferQuestionScoring(question).average) ??
      questions.find(compatible)
    );
  }
  return questions.find(compatible);
}

export function getPresetColumns(
  preset: TablePreset,
  questions: ReportQuestion[],
  question?: ReportQuestion,
  metrics: ReportMetricDefinition[] = [],
): TableColumn[] {
  const columns: TableColumn[] = [{ id: 'identity', label: '', type: ColumnType.LABEL }];
  const add = (type: ColumnType, binding = question) => {
    const column = makeTableColumn(`preset-${columns.length}`, type, { question: binding });
    if (column) columns.push(column);
  };
  if (preset === 'text') {
    add(ColumnType.TEAM);
    add(ColumnType.TEXT);
    add(ColumnType.THEMES);
  } else if (preset === 'choices') {
    add(ColumnType.RESPONDENT_COUNT);
    add(ColumnType.CHOICES);
  } else {
    add(ColumnType.RESPONDENT_COUNT);
    add(ColumnType.RESPONSE_RATE);
    metrics.forEach((metric) =>
      columns.push({ id: `metric-${metric.id}`, type: ColumnType.CUSTOM_METRIC, label: '', metricId: metric.id }),
    );
    if (preset === 'wide') {
      add(ColumnType.CHOICES, questions.find(isChoiceQuestion));
      const textQuestion = questions.find((candidate) => candidate.type === QuestionType.Freetext);
      // Do not let an absent text question fall back to the selected numeric question.
      if (textQuestion) {
        add(ColumnType.TEXT, textQuestion);
        add(ColumnType.THEMES, textQuestion);
      }
    }
  }
  return columns;
}
